import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils/cn';

export type Variant = 'filled' | 'outline' | 'link' | 'icon';
export type Size = 'small' | 'medium' | 'large' | 'icon';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isDanger?: boolean;
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, isDanger, asChild = false, ...props }, ref) => {
    const buttonVariants = cva(
      'inline-flex items-center justify-center rounded-md text-xs font-medium focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-disableBg disabled:text-disableText leading-normal',
      {
        variants: {
          variant: {
            filled: `${
              isDanger
                ? 'bg-red-1 text-white hover:bg-red-1-hover active:bg-red-1-active'
                : 'bg-blue-1 hover:bg-blue-1-hover active:bg-blue-1-active'
            } text-white`,
            outline: `${
              isDanger
                ? 'border border-red-1 text-red-1 hover:bg-red-1-hover hover:border-red-1-hover disabled:border disabled:border-disableBorder hover:text-white active:bg-red-1-active active:border-red-1-active'
                : 'border border-blue-1 text-blue-1 hover:bg-blue-1-hover hover:border-blue-1-hover disabled:border disabled:border-disableBorder hover:text-white active:bg-blue-1-active active:border-blue-1-active'
            }
              `,
            link: `${
              isDanger
                ? 'text-red-1 hover:text-red-1-hover active:text-red-1-active'
                : 'text-blue-1 hover:text-blue-1-hover active:text-blue-1-active'
            } disabled:bg-transparent disabled:text-disableText disabled:border-none`,
            icon: `bg-hover hover:bg-border text-text hover:text-text shadow-md disabled:bg-transparent disabled:text-disableText disabled:border-none`,
          },
          size: {
            small: 'py-1 px-4 h-7',
            medium: 'py-1 px-4 h-8',
            large: 'py-[1.25rem] px-[0.9375rem] h-[3.75rem]',
            icon: 'h-9 w-9',
          },
        },
        defaultVariants: {
          variant: 'filled',
          size: 'medium',
        },
      }
    );

    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button };
